import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Landmark, Plus } from 'lucide-react';
import { Label } from '@/components/atoms/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { ContactBillToFormSheet } from '@/components/organisms/contacts/ContactBillToFormSheet';
import { useBillTo } from '@/hooks/contacts/useBillTo';
import { normalizeContact } from '@/hooks/contacts/useContacts';
import { apiGet, operationsClient } from '@/lib/api';
import { getContactDisplayName } from '@/lib/contacts';
import { contactKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import type { BillTo, Contact } from '@/types/contact';

/**
 * Bill-to selector — shared by the Job and Estimate forms. Card trigger + popover
 * matching the neighbouring Contact person / Email / Phone / Address cards. Lists
 * the contact's catalog (`contact.billTo[]`) + "Self" + "Add bill to" (creates an
 * entry via the shared sheet). Emits `billToId` (null = Self); the backend
 * resolves the immutable snapshot.
 */
export function BillToSelector({
  contactId,
  currentContact,
  value,
  onChange,
  customSelected = false,
  onCustomSelect,
}: {
  contactId?: string;
  currentContact?: Contact | null;
  /** Persisted `billToId`: string = catalog entry · null = Self · undefined = use default. */
  value: string | null | undefined;
  onChange: (billToId: string | null) => void;
  /** Estimate forms can represent a manual billing override as Custom while persisting billToId=null. */
  customSelected?: boolean;
  onCustomSelect?: () => void;
}) {
  const { data: entries = [] } = useBillTo(contactId ?? '');
  const [open, setOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Catalog entry ids captured when "Add bill to" opens, so the newly created
  // entry can be auto-selected once the catalog query refetches.
  const [idsBeforeAdd, setIdsBeforeAdd] = useState<string[] | null>(null);

  useEffect(() => {
    if (idsBeforeAdd === null) return;
    const created = entries.find((entry) => !idsBeforeAdd.includes(entry.id));
    if (created) {
      onChange(created.id);
      setIdsBeforeAdd(null);
    }
  }, [entries, idsBeforeAdd, onChange]);

  // Resolve each entry's destination contact name (same as ContactBillToSection).
  const relatedContactIds = useMemo(
    () =>
      Array.from(
        new Set(
          entries.map((entry) => entry.contactId).filter((id) => Boolean(id) && id !== contactId),
        ),
      ),
    [entries, contactId],
  );
  const relatedQueries = useQueries({
    queries: relatedContactIds.map((relatedId) => ({
      queryKey: contactKeys.detail(relatedId),
      queryFn: async () =>
        normalizeContact(
          await apiGet<Record<string, unknown>>(operationsClient, `/contacts/${relatedId}`),
        ),
      enabled: Boolean(relatedId),
      staleTime: 60_000,
    })),
  });
  const namesById = useMemo(() => {
    const map = new Map<string, string>();
    relatedQueries.forEach((query, index) => {
      const id = relatedContactIds[index];
      if (query.data && id) map.set(id, getContactDisplayName(query.data));
    });
    return map;
  }, [relatedQueries, relatedContactIds]);

  function destinationName(entry: BillTo): string {
    if (entry.contactId === contactId) {
      return currentContact ? getContactDisplayName(currentContact) : 'This contact';
    }
    return namesById.get(entry.contactId) ?? 'Loading…';
  }

  const defaultEntryId = entries.find((entry) => entry.isDefault)?.id ?? null;
  // undefined → fall back to the contact's default entry (else Self).
  const effectiveId = value === undefined ? defaultEntryId : value;
  const selectedEntry = effectiveId ? entries.find((entry) => entry.id === effectiveId) : undefined;

  const triggerTitle = customSelected ? 'Custom' : selectedEntry ? selectedEntry.label : 'Self';
  const triggerSub = customSelected
    ? 'Manual billing address'
    : selectedEntry
      ? destinationName(selectedEntry)
      : 'This contact';

  function openAddSheet() {
    if (!contactId) return;
    setIdsBeforeAdd(entries.map((entry) => entry.id));
    setOpen(false);
    setSheetOpen(true);
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor="bill-to">Bill to</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id="bill-to"
            type="button"
            disabled={!contactId}
            className="relative flex w-full cursor-pointer items-center gap-3 rounded-lg border border-border bg-muted/40 p-3 text-left text-sm transition-colors hover:bg-muted/70 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{triggerTitle}</p>
              <p className="truncate text-xs text-muted-foreground">{triggerSub}</p>
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2" align="start">
          <div className="space-y-1">
            <BillToOption
              active={!selectedEntry && !customSelected}
              title="Self"
              sub="This contact"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            />
            {onCustomSelect ? (
              <BillToOption
                active={customSelected}
                title="Custom"
                sub="Enter a one-off billing address"
                onClick={() => {
                  onCustomSelect();
                  setOpen(false);
                }}
              />
            ) : null}
            {entries.map((entry) => (
              <BillToOption
                key={entry.id}
                active={selectedEntry?.id === entry.id}
                title={entry.label}
                sub={destinationName(entry)}
                onClick={() => {
                  onChange(entry.id);
                  setOpen(false);
                }}
              />
            ))}
            <button
              type="button"
              onClick={openAddSheet}
              className="flex w-full items-center gap-2 rounded-md p-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted"
            >
              <Plus className="h-3.5 w-3.5 shrink-0" />
              Add bill to
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {contactId ? (
        <ContactBillToFormSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          mode="create"
          contactId={contactId}
          currentContact={currentContact}
          entriesCount={entries.length}
        />
      ) : null}
    </div>
  );
}

function BillToOption({
  active,
  title,
  sub,
  onClick,
}: {
  active: boolean;
  title: string;
  sub: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-md p-2.5 text-left text-sm transition-colors',
        active ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
      )}
    >
      <Landmark className="h-3.5 w-3.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{title}</p>
        <p className="truncate text-xs text-muted-foreground">{sub}</p>
      </div>
      {active && <Check className="h-3.5 w-3.5 shrink-0" />}
    </button>
  );
}
